import { Check, Users } from 'lucide-react'
import type { Event } from './types'
import { useLanguage } from '@/contexts/LanguageContext'

/**
 * Etkinlik listesinin bir satırı.
 *
 * Maçlar sekmesindeki `MatchRow` ile aynı biçimdedir ve bilerek öyle: iki liste
 * yan yana iki ayrı ürün gibi okunmamalı. Satır **tam kanama**dır — kenarda
 * yüzen bir kart değil — ve seçili olan kendini soldaki kızıl çubukla söyler;
 * kızıl burada da yüzey değil işaret.
 */

interface Props {
  event: Event
  selected: boolean
  isSignedUp?: boolean
  onClick: () => void
}

export default function EventListItem({ event, selected, isSignedUp, onClick }: Props) {
  const { T, lang } = useLanguage()
  const locale = lang === 'tr' ? 'tr-TR' : 'en-GB'

  const d = event.start_time ? new Date(event.start_time * 1000) : null
  const dayLabel = d && `${T.calendar.days[(d.getDay() + 6) % 7]}, ${d.getDate()} ${T.calendar.monthsShort[d.getMonth()]}`
  const timeLabel = d && d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })

  const pending = event.sign_ups.filter(
    s => s.class_name === 'Bench' || s.class_name === 'Tentative',
  ).length

  return (
    <button
      onClick={onClick}
      aria-current={selected}
      className={`relative w-full border-b border-white/10 px-3 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/60 ${
        selected ? 'bg-white/[0.06]' : 'hover:bg-white/[0.03]'
      }`}
    >
      {selected && <span className="absolute inset-y-0 left-0 w-0.5 bg-primary" />}

      <div className="flex items-start justify-between gap-2">
        {/* Etkinlik adı veridir: büyütülmez. */}
        <p className="line-clamp-2 text-sm font-semibold leading-tight">{event.title}</p>
        <span className={`flex shrink-0 items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[10px] tabular-nums ${
          selected
            ? 'border-primary/50 bg-primary/10 text-primary'
            : 'border-white/10 bg-white/5 text-muted-foreground'
        }`}>
          <Users className="h-2.5 w-2.5" />
          {event.active_count}
          {pending > 0 && <span className="opacity-50"> (+{pending})</span>}
        </span>
      </div>

      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <p className="text-[11px] text-muted-foreground">
          {d ? `${dayLabel} · ${timeLabel}` : event.date}
        </p>
        {isSignedUp !== undefined && (
          isSignedUp ? (
            <span className="flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-emerald-400/80">
              <Check className="h-2.5 w-2.5" />
              {T.events.signedUp}
            </span>
          ) : (
            // "Senden bir şey bekleniyor" — panelde kızılın anlamı budur.
            <span className="animate-pulse-fade text-[9px] font-bold uppercase tracking-[0.12em] text-primary">
              {T.events.notSignedUp}
            </span>
          )
        )}
      </div>
    </button>
  )
}
